import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts/core"
import { BarChart } from "echarts/charts"
import { AriaComponent, GridComponent, LegendComponent, TooltipComponent } from "echarts/components"
import { CanvasRenderer } from "echarts/renderers"

echarts.use([BarChart, AriaComponent, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer])

const SMALL_SCREEN_MAX_WIDTH = 800
const LABEL_FONT_SIZE = 12
const SMALL_SCREEN_QUARTER_FONT_SIZE = 10
const SMALL_SCREEN_STACK_FONT_SIZE = 8
const STACK_LABEL_SPACING = 4
const STACK_LABELS_AXIS_MARGIN = 28
const DEFAULT_AXIS_MARGIN = 8
const Y_AXIS_ALLOWANCE = 60
const BAR_CATEGORY_GAP = 0.5
const SMALL_SCREEN_BAR_CATEGORY_GAP = 0.4
const LABEL_COLOR = "rgb(102, 102, 102)"
const BAR_BORDER_RADIUS = 6
const LEGEND_FONT_SIZE = 12
const FONT_FAMILY = "sans-serif"
const LEGEND_SYMBOL_WIDTH = 25
const LEGEND_TEXT_OFFSET = 5
const LEGEND_ITEM_GAP = 10
const LEGEND_PADDING = 5
const LEGEND_ROW_HEIGHT = 24
const LEGEND_BLOCK_GAP = 10
const LEGEND_STYLE = {
  orient: "vertical",
  bottom: 0,
  itemGap: LEGEND_ITEM_GAP,
  padding: LEGEND_PADDING,
  textStyle: { fontSize: LEGEND_FONT_SIZE, fontFamily: FONT_FAMILY }
}
const AXIS_LABELS_CLEARANCE = 22
const compactNumber = new Intl.NumberFormat(undefined, { notation: "compact" })

const textWidth = (text, fontSize) => echarts.format.getTextRect(text, `${fontSize}px ${FONT_FAMILY}`).width

const tooltipLabel = ({ marker, name, seriesName, value }) =>
  `${name}<br/>${marker} ${seriesName}: <b>${value.toLocaleString()}</b>`

export default class extends Controller {
  static targets = ["chart", "currencySelector"]
  static values = { series: Array, categories: Array, stackLabels: Object, yAxisTitle: String, height: Number }

  connect() {
    this.dataType = "count"
    this.currency = "CHF"
    this.legendSelection = Object.fromEntries(this.discountSeries().map(({ name }) => [name, false]))
    this.chartTarget.style.height = `${this.heightValue}px`
    this.chart = echarts.init(this.chartTarget)
    this.chart.on("legendselectchanged", ({ selected }) => {
      this.legendSelection = selected
      this.redraw()
    })
    this.redraw()
    this.resizeObserver = new ResizeObserver(() => {
      this.chart.resize()
      this.redraw()
    })
    this.resizeObserver.observe(this.chartTarget)
  }

  disconnect() {
    this.resizeObserver.disconnect()
    this.chart.dispose()
  }

  selectDataType(event) {
    this.dataType = event.target.value
    this.currencySelectorTarget.hidden = this.dataType === "count"
    this.redraw()
  }

  selectCurrency(event) {
    this.currency = event.target.value
    this.redraw()
  }

  redraw() {
    const legendLayout = this.legendLayout()

    this.fitHeightToLegend(legendLayout.rowCount)
    this.chart.setOption(this.chartOption(legendLayout), { replaceMerge: ["legend"] })
  }

  fitHeightToLegend(legendRowCount) {
    const rowsBeyondStackRows = Math.max(0, legendRowCount - Object.keys(this.stackLabelsValue).length)
    const height = `${this.heightValue + rowsBeyondStackRows * LEGEND_ROW_HEIGHT}px`
    if (this.chartTarget.style.height === height) return

    this.chartTarget.style.height = height
    this.chart.resize()
  }

  chartOption({ legend, grid }) {
    const barSeries = this.barSeries()

    return {
      aria: { enabled: true, label: { description: this.ariaDescription(barSeries) } },
      tooltip: { trigger: "item", formatter: tooltipLabel },
      legend,
      grid: { ...grid, left: 10, right: 10, top: 20, outerBoundsMode: "same" },
      xAxis: {
        type: "category",
        data: this.categoriesValue,
        axisLine: { show: false, onZero: false },
        axisTick: { show: false },
        axisLabel: { margin: this.stackLabelsFit() ? STACK_LABELS_AXIS_MARGIN : DEFAULT_AXIS_MARGIN, interval: 0, fontSize: this.labelFontSize(SMALL_SCREEN_QUARTER_FONT_SIZE) }
      },
      yAxis: {
        type: "value",
        name: this.yAxisTitleValue,
        nameLocation: "middle",
        nameGap: 40,
        axisLabel: { formatter: (value) => compactNumber.format(value) }
      },
      series: [...barSeries, ...this.stackLabelSeries()]
    }
  }

  ariaDescription(barSeries) {
    return barSeries
      .filter((series) => this.isShown(series) && series.data.length)
      .map(({ name, data }) => `${name}: ${data.map((point) => `${point.name} ${point.value ?? 0}`).join(", ")}`)
      .join(". ")
  }

  barCategoryGap() {
    return this.isSmallScreen() ? SMALL_SCREEN_BAR_CATEGORY_GAP : BAR_CATEGORY_GAP
  }

  stackLabelsFit() {
    const fontSize = this.labelFontSize(SMALL_SCREEN_STACK_FONT_SIZE)
    const labelWidths = Object.values(this.stackLabelsValue).map((label) => textWidth(label, fontSize))
    const categoryWidth = (this.chart.getWidth() - Y_AXIS_ALLOWANCE) / this.categoriesValue.length
    const barWidth = categoryWidth * (1 - this.barCategoryGap()) / labelWidths.length

    return labelWidths.slice(1).every((width, index) => (labelWidths[index] + width) / 2 + STACK_LABEL_SPACING <= barWidth)
  }

  isSmallScreen() {
    return this.chart.getWidth() <= SMALL_SCREEN_MAX_WIDTH
  }

  labelFontSize(smallScreenFontSize) {
    return this.isSmallScreen() ? smallScreenFontSize : LABEL_FONT_SIZE
  }

  barSeries() {
    return this.roundStackEnds(this.seriesValue.map(({ custom, ...series }) => ({
      ...series,
      type: "bar",
      stackOrder: "seriesDesc",
      barGap: "0%",
      barCategoryGap: `${this.barCategoryGap() * 100}%`,
      data: custom.discount ? this.discountPoints(custom.data) : this.statePoints(series.data, custom.data)
    })))
  }

  roundStackEnds(allSeries) {
    return allSeries.map((series) => ({
      ...series,
      data: series.data.map((point, index) => {
        if (!this.isStackEnd(allSeries, series, index)) return point

        return { ...point, itemStyle: { borderRadius: point.value > 0 ? [BAR_BORDER_RADIUS, BAR_BORDER_RADIUS, 0, 0] : [0, 0, BAR_BORDER_RADIUS, BAR_BORDER_RADIUS] } }
      })
    }))
  }

  isStackEnd(allSeries, series, index) {
    const direction = Math.sign(series.data[index].value)
    if (!direction) return false

    const outermost = allSeries.find((candidate) =>
      candidate.stack === series.stack && this.isShown(candidate) && Math.sign(candidate.data[index]?.value) === direction)

    return outermost === series
  }

  isShown(series) {
    return this.legendSelection[series.name] !== false
  }

  statePoints(points, quarters) {
    return points.map(({ name }, index) => ({ name, value: this.quarterValue(quarters[index]) }))
  }

  quarterValue(quarter) {
    return this.dataType === "count" ? quarter.count : quarter.total_price[this.currency]
  }

  discountPoints(quarters) {
    if (this.dataType === "count") return []

    return quarters.map((quarter) => ({ name: quarter.tooltip, value: quarter[this.currency] }))
  }

  stackLabelSeries() {
    return Object.entries(this.stackLabelsValue).map(([stack, label]) => ({
      type: "bar",
      stack,
      silent: true,
      tooltip: { show: false },
      itemStyle: { color: "transparent" },
      label: { show: this.stackLabelsFit(), position: "bottom", color: LABEL_COLOR, fontSize: this.labelFontSize(SMALL_SCREEN_STACK_FONT_SIZE), formatter: label },
      data: this.categoriesValue.map(() => 0)
    }))
  }

  discountSeries() {
    return this.seriesValue.filter(({ custom }) => custom.discount)
  }

  legendLayout() {
    const stackRows = Object.keys(this.stackLabelsValue).map((stack) => this.legendSeries(stack).map(({ name }) => name))
    const columns = stackRows[0].map((_, column) => stackRows.map((row) => row[column]))
    const layoutsFromWidestToNarrowest = [columns, stackRows, [stackRows.flat()]].map((groups) => this.legendBlocks(groups))

    return layoutsFromWidestToNarrowest.find(({ width }) => width <= this.chart.getWidth()) ?? layoutsFromWidestToNarrowest.at(-1)
  }

  legendBlocks(groups) {
    const blockWidths = groups.map((names) => this.legendBlockWidth(names))
    const width = blockWidths.reduce((sum, blockWidth) => sum + blockWidth, 0) + LEGEND_BLOCK_GAP * (groups.length - 1)
    const blockLefts = blockWidths.map((_, index) =>
      (this.chart.getWidth() - width) / 2 + blockWidths.slice(0, index).reduce((sum, blockWidth) => sum + blockWidth + LEGEND_BLOCK_GAP, 0))

    const rowCount = Math.max(...groups.map((names) => names.length))

    return {
      width,
      rowCount,
      legend: groups.map((names, index) => ({ ...LEGEND_STYLE, left: blockLefts[index], data: names, selected: this.legendSelection })),
      grid: { bottom: rowCount * LEGEND_ROW_HEIGHT + AXIS_LABELS_CLEARANCE }
    }
  }

  legendBlockWidth(names) {
    const widestText = Math.max(...names.map((name) => textWidth(name, LEGEND_FONT_SIZE)))

    return LEGEND_SYMBOL_WIDTH + LEGEND_TEXT_OFFSET + widestText + 2 * LEGEND_PADDING
  }

  legendSeries(stack) {
    return this.seriesValue.filter((series) =>
      series.stack === stack && (this.dataType === "amount" || !series.custom.discount))
  }
}
